/**
 * Dining & Events — Bookings: the printed CARRIAGE REPORT.
 *
 * Every word on the page is 14pt or larger — these sheets are read standing
 * in a carriage, not at a desk. A 17-column table cannot be 14pt on A4 in
 * either orientation, so the report is laid out as GROUPS and BLOCKS, not a
 * grid. The groups are the two "Sort report" keys (bookingsSort.tsx):
 *
 *   ┌ green band ── the PRIMARY group, e.g. "Wednesday 16 September 2026" ──┐
 *   │  the SECONDARY group, e.g. "Pullman - Car 79  ·  6 bookings"         │
 *   │  ┌ one block per booking line ──────────────────────────────────────┐ │
 *   │  │ Moore  ·  Seats C1,C2  ·  Booking 283506  ·  07984 621609 (bold) │ │
 *   │  │ 12:35  ·  MOOR  ·  Moorlander Pullman - Premium Seat  ·  £205.00 │ │
 *   │  │ Expected 6   Ordered 4   Variance -2   Over/short Yes   Allergens No │
 *   │  │ Notes: paid, conf sent 02/09 …                                   │ │
 *   │  └──────────────────────────────────────────────────────────────────┘ │
 *
 * Whatever the headings already say (the date, the carriage…) is left out of
 * the blocks. Over/short and Allergens print RED when "Yes".
 *
 * The rows arrive ALREADY in the order the grid shows (`displayRows` — the one
 * owner of the report's order); this file never re-sorts. A group starts when
 * the primary / secondary key value changes. Date, Time and Carriage groups
 * start a fresh page; a block is never split across pages; a group that runs
 * on is re-headed "(continued)". PORTRAIT ONLY: in landscape the Bookings report
 * keeps its original wide table (downloadBookingsPdf) — that sheet was never to change.
 */
import { jsPDF } from "jspdf";
import { cleanNote, money, prettyDate, timeLabel, type Row } from "./bookingsShared";
import { describeSort, groupValue, sortOption, type SortSpec } from "./bookingsSort";
import type { PdfOrientation } from "./orientation";

const PT = 0.352778; // 1pt in mm
/** The floor — nothing on the page prints smaller than this. */
export const BODY_PT = 14;
const SUB_PT = 15;
const BAND_PT = 18;
const TITLE_PT = 20;
const LEADING = 1.3;

type Rgb = [number, number, number];
const GREEN: Rgb = [31, 77, 63];
const GREEN_DEEP: Rgb = [11, 61, 46];
const WHITE: Rgb = [255, 255, 255];
const INK: Rgb = [30, 30, 30];
const MUTED: Rgb = [92, 92, 92];
const CREAM: Rgb = [250, 248, 242];
const RULE: Rgb = [214, 210, 200];
const RED: Rgb = [198, 40, 40];

const lh = (pt: number) => pt * PT * LEADING;
const text = (v: unknown) => (v == null ? "" : String(v).trim());
const isYes = (v: unknown) => text(v).toLowerCase() === "yes";
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** jsPDF's built-in Helvetica knows Windows-1252 only: keep those characters,
 *  map the common look-alikes (minus sign, arrow, hard space), drop the rest
 *  (emoji and the like) rather than print garbage. */
const CP1252_EXTRA = new Set("€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ");
export function sanitise(s: string): string {
  let out = "";
  for (const ch of s.replace(/\u2212/g, "-").replace(/\u2192/g, "->").replace(/\u00A0/g, " ")) {
    const cp = ch.codePointAt(0) ?? 0;
    if (cp < 0x100 || CP1252_EXTRA.has(ch)) out += ch;
  }
  return out;
}

interface Para { kind: "para"; lines: string[]; pt: number; bold: boolean; color: Rgb }
interface Fig { label: string; value: string; alert: boolean; labelW: number; w: number }
interface FigRow { cells: { fig: Fig; x: number }[] }
interface Figures { kind: "figures"; rows: FigRow[] }
interface Block { items: (Para | Figures)[]; height: number }
interface Heading { lines: string[]; right: string; height: number }

export function buildCarriageReportPdf(
  rows: Row[],
  range: { from: string; to: string },
  orientation: PdfOrientation,
  spec: SortSpec,
  countingLabel?: string
): jsPDF {
  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 12; // page margin
  const X = M;
  const W = pageW - 2 * M;
  const PADX = 3; // block padding, sides
  const PADY = 2.2; // block padding, top and bottom
  const GAP = 0.8; // between the paragraphs of a block
  const TEXT_W = W - 2 * PADX;
  const FOOT_Y = pageH - 10;
  const BOTTOM = FOOT_Y - 4; // content ends above this

  const primary = sortOption(spec.primary);
  const secondary = sortOption(spec.secondary);
  /** A field the headings do not already say, so the block must. */
  const shown = (key: string) => key !== primary.key && key !== secondary.key;

  const font = (pt: number, bold: boolean, color: Rgb) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(pt);
    doc.setTextColor(color[0], color[1], color[2]);
  };
  const wrap = (s: string, pt: number, bold: boolean, width: number): string[] => {
    font(pt, bold, INK);
    const lines = doc.splitTextToSize(sanitise(s), width) as string[];
    return lines.length ? lines : [""];
  };
  const put = (s: string, x: number, y: number, align: "left" | "right" = "left") => {
    doc.text(sanitise(s), x, y, { baseline: "top", align });
  };
  const width = (s: string) => doc.getTextWidth(sanitise(s));

  // Distinct bookings per group — the counts printed in the headings.
  const pBookings = new Map<string, Set<string>>();
  const sBookings = new Map<string, Set<string>>();
  const count = (m: Map<string, Set<string>>, k: string, b: string) => {
    const s = m.get(k);
    if (s) s.add(b);
    else m.set(k, new Set([b]));
  };
  const sKeyOf = (p: string, s: string) => `${p}\u0001${s}`;
  rows.forEach((r, i) => {
    const p = groupValue(primary, r);
    const b = text(r.Booking) || `row-${i}`;
    count(pBookings, p, b);
    count(sBookings, sKeyOf(p, groupValue(secondary, r)), b);
  });

  // ---- one booking line \u2192 a block ------------------------------------------
  function planBlock(row: Row): Block {
    const items: (Para | Figures)[] = [];
    const para = (s: string, bold: boolean, color: Rgb) => {
      if (s) items.push({ kind: "para", lines: wrap(s, BODY_PT, bold, TEXT_W), pt: BODY_PT, bold, color });
    };

    const who: string[] = [];
    if (shown("AccName") && text(row.AccName)) who.push(text(row.AccName));
    if (shown("Seats") && text(row.Seats)) who.push(`Seats ${text(row.Seats)}`);
    if (text(row.Booking)) who.push(`Booking ${text(row.Booking)}`);
    if (text(row.TelephoneNo)) who.push(text(row.TelephoneNo));
    para(who.join("  ·  "), true, INK);

    const what: string[] = [];
    if (shown("JourneyDate") && text(row.JourneyDate)) what.push(`${text(row.Day)} ${prettyDate(row.JourneyDate)}`.trim());
    if (shown("Time") && timeLabel(row.Time)) what.push(timeLabel(row.Time));
    if (text(row.Journey)) what.push(text(row.Journey));
    if (shown("Carriage") && text(row.Carriage)) what.push(text(row.Carriage));
    if (text(row.Item)) what.push(text(row.Item));
    if (money(row.Price)) what.push(money(row.Price));
    para(what.join("  ·  "), false, INK);

    // The figures strip: five labelled values — justified across one line when
    // they fit (equal gaps, the last flush right), flowed onto more lines when not.
    const specs: [string, unknown, boolean][] = [
      ["Expected", row.Expected, false],
      ["Ordered", row.Ordered, false],
      ["Variance", row.Variance, false],
      ["Over/short", row.OverOrShort, isYes(row.OverOrShort)],
      ["Allergens", row.AllergensTicked, isYes(row.AllergensTicked)],
    ];
    const figs: Fig[] = specs.map(([label, v, alert]) => {
      const value = text(v) || "-";
      font(BODY_PT, false, INK);
      const labelW = width(`${label} `);
      font(BODY_PT, true, INK);
      return { label, value, alert, labelW, w: labelW + width(value) };
    });
    const figRows: FigRow[] = [];
    const used = figs.reduce((sum, f) => sum + f.w, 0);
    if (used + (figs.length - 1) * 6 <= TEXT_W) {
      const gap = (TEXT_W - used) / (figs.length - 1);
      let x = 0;
      figRows.push({
        cells: figs.map((fig) => {
          const cell = { fig, x };
          x += fig.w + gap;
          return cell;
        }),
      });
    } else {
      const FGAP = 7;
      let cells: { fig: Fig; x: number }[] = [];
      let x = 0;
      for (const fig of figs) {
        if (cells.length && x + fig.w > TEXT_W) {
          figRows.push({ cells });
          cells = [];
          x = 0;
        }
        cells.push({ fig, x });
        x += fig.w + FGAP;
      }
      if (cells.length) figRows.push({ cells });
    }
    items.push({ kind: "figures", rows: figRows });

    const note = cleanNote(row.Notes);
    para(note ? `Notes: ${note}` : "", false, MUTED);

    let height = 2 * PADY - GAP;
    for (const it of items) {
      height += (it.kind === "para" ? it.lines.length : it.rows.length) * lh(BODY_PT) + GAP;
    }
    return { items, height };
  }

  function drawBlock(b: Block, y: number, shade: boolean) {
    if (shade) {
      doc.setFillColor(...CREAM);
      doc.rect(X, y, W, b.height, "F");
    }
    let cy = y + PADY;
    for (const it of b.items) {
      if (it.kind === "para") {
        font(it.pt, it.bold, it.color);
        for (const ln of it.lines) {
          put(ln, X + PADX, cy);
          cy += lh(it.pt);
        }
      } else {
        for (const r of it.rows) {
          for (const { fig, x } of r.cells) {
            font(BODY_PT, false, MUTED);
            put(`${fig.label} `, X + PADX + x, cy);
            font(BODY_PT, true, fig.alert ? RED : INK);
            put(fig.value, X + PADX + x + fig.labelW, cy);
          }
          cy += lh(BODY_PT);
        }
      }
      cy += GAP;
    }
    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.2);
    doc.line(X, y + b.height, X + W, y + b.height);
  }

  // ---- headings ------------------------------------------------------------
  const BAND_AFTER = 2;
  const SUB_AFTER = 1.5;
  function planBand(title: string, right: string): Heading {
    font(BODY_PT, false, WHITE);
    const rightW = width(right) + 4;
    const lines = wrap(title, BAND_PT, true, W - 2 * PADX - rightW);
    return { lines, right, height: lines.length * lh(BAND_PT) + 5 };
  }
  function drawBand(h: Heading, y: number) {
    doc.setFillColor(...GREEN);
    doc.rect(X, y, W, h.height, "F");
    font(BAND_PT, true, WHITE);
    let cy = y + 2.5;
    for (const ln of h.lines) {
      put(ln, X + PADX, cy);
      cy += lh(BAND_PT);
    }
    font(BODY_PT, false, WHITE);
    put(h.right, X + W - PADX, y + 2.5 + (BAND_PT - BODY_PT) * PT, "right");
  }
  function planSub(title: string): Heading {
    const lines = wrap(title, SUB_PT, true, W - 2 * PADX);
    return { lines, right: "", height: lines.length * lh(SUB_PT) + 4 };
  }
  function drawSub(h: Heading, y: number) {
    font(SUB_PT, true, GREEN_DEEP);
    let cy = y + 1.5;
    for (const ln of h.lines) {
      put(ln, X + PADX, cy);
      cy += lh(SUB_PT);
    }
    doc.setDrawColor(...GREEN);
    doc.setLineWidth(0.4);
    doc.line(X, y + h.height - 1.2, X + W, y + h.height - 1.2);
  }

  // ---- pages ---------------------------------------------------------------
  const printedOn = new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const title = "Dining & Events — Bookings";
  const rangeLabel = `${prettyDate(range.from)} to ${prettyDate(range.to)}`;
  let y = 0;

  // The group a run-on page continues under.
  let curPTitle = "";
  let curPRight = "";
  let curSTitle = "";

  function firstPage() {
    font(TITLE_PT, true, GREEN_DEEP);
    put(title, X, 10);
    y = 10 + lh(TITLE_PT) + 1;
    const sub = wrap(
      `${rangeLabel}  ·  ${plural(rows.length, "row")}  ·  sorted by ${describeSort(spec)}${countingLabel ? `  ·  ${countingLabel}` : ""}  ·  printed ${printedOn}`,
      BODY_PT, false, W
    );
    font(BODY_PT, false, MUTED);
    for (const ln of sub) {
      put(ln, X, y);
      y += lh(BODY_PT);
    }
    y += 4;
  }
  function newPage(cont: { band: boolean; sub: boolean }) {
    doc.addPage();
    font(BODY_PT, false, MUTED);
    put(`${title}  ·  ${rangeLabel}`, X, 8);
    y = 8 + lh(BODY_PT) + 4;
    if (cont.band) {
      const h = planBand(`${curPTitle} (continued)`, curPRight);
      drawBand(h, y);
      y += h.height + BAND_AFTER;
    }
    if (cont.sub) {
      const h = planSub(`${curSTitle} (continued)`);
      drawSub(h, y);
      y += h.height + SUB_AFTER;
    }
  }

  firstPage();
  if (rows.length === 0) {
    font(BODY_PT, false, INK);
    put("No bookings in this date range.", X, y);
  }

  let curP: string | null = null;
  let curS: string | null = null;
  let shade = false;
  for (const row of rows) {
    const pKey = groupValue(primary, row);
    const sKey = groupValue(secondary, row);
    const newP = curP === null || pKey !== curP;
    const newS = newP || curS === null || sKey !== curS;
    const block = planBlock(row);

    let band: Heading | null = null;
    let sub: Heading | null = null;
    if (newP) {
      curPTitle = primary.heading(row);
      curPRight = plural(pBookings.get(pKey)?.size ?? 0, "booking");
      band = planBand(curPTitle, curPRight);
    }
    if (newS) {
      curSTitle = secondary.heading(row);
      const n = sBookings.get(sKeyOf(pKey, sKey))?.size ?? 0;
      sub = planSub(`${curSTitle}  ·  ${plural(n, "booking")}`);
    }

    // A heading never sits alone at the foot of a page: it moves with its first block.
    const need = (band ? band.height + BAND_AFTER : 0) + (sub ? sub.height + SUB_AFTER : 0) + block.height;
    if (newP && curP !== null && primary.freshPage) newPage({ band: false, sub: false });
    else if (y + need > BOTTOM) newPage({ band: !newP, sub: !newS });

    if (band) {
      drawBand(band, y);
      y += band.height + BAND_AFTER;
    }
    if (sub) {
      drawSub(sub, y);
      y += sub.height + SUB_AFTER;
      shade = false;
    }
    drawBlock(block, y, shade);
    y += block.height;
    shade = !shade;
    curP = pKey;
    curS = sKey;
  }

  // Footers — the total is known now, so no placeholder arithmetic.
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    font(BODY_PT, false, MUTED);
    put(`Printed ${printedOn}`, X, FOOT_Y);
    put(`Page ${p} of ${total}`, X + W, FOOT_Y, "right");
  }
  return doc;
}

/** Build and download — a direct download via doc.save(), never a popup. */
export function downloadCarriageReportPdf(
  rows: Row[],
  range: { from: string; to: string },
  orientation: PdfOrientation,
  spec: SortSpec,
  countingLabel?: string
): void {
  buildCarriageReportPdf(rows, range, orientation, spec, countingLabel).save(`dining-bookings-${range.from}_to_${range.to}.pdf`);
}
